import { jsx as _jsx } from "react/jsx-runtime";
/**
 * The React face of the step-up wrapper and the kernel session claims (kernel apps, WS-17).
 *
 * The kernel shell (WS-15) mounts {@link StepUpProvider} with its presenter. A component on the
 * kernel origin wraps its fetch with `useStepUp().wrap(fetch)`, and reads the tier-2 window with
 * {@link useKernelSession}. A component on `<dp>` mounts no provider: `useStepUp` then answers a
 * state with no presenter, and every step-up 403 reaches its caller as a plain 403 (index defaults
 * row "refuse over accept").
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { STEP_UP_WINDOW_SECONDS } from '@zion/sdk-contracts';
import { useAuth } from '../auth-context.js';
import { refreshAndNotify } from '../token-manager.js';
import { dataPlaneKernelOrigin, kernelRefusalOf } from './kernel-refusal.js';
import { isStepUpWindowOpen, kernelSessionClaimsOf, stepUpWindowClosesAt, } from './kernel-session.js';
import { createStepUpFetch } from './step-up-fetch.js';
/** The state of no presenter: the answer outside a provider and of `presenter: null`. */
const NO_STEP_UP = Object.freeze({
    available: false,
    pending: null,
    lastOutcome: null,
    wrap: (base) => base,
    approve: () => Promise.resolve('dismissed'),
});
const StepUpContext = createContext(NO_STEP_UP);
/**
 * Puts one queue around `presenter` for every wrapper and every `approve` below it.
 *
 * The queue shows one presentation at a time across the whole provider. It sets `pending` before
 * each presentation and clears it after, and sets `lastOutcome` from each decision. A presenter
 * rejection leaves `lastOutcome` as it was and reaches the caller. A queued presentation whose
 * turn comes after the provider lost its presenter (`presenter: null`) resolves `dismissed` and
 * shows nothing. An `approved` decision of `approve` runs `refreshSession` and ignores its
 * rejection, as the wrapper does.
 *
 * A turn whose signal aborted before the turn came shows nothing: the presenter gets no call,
 * and `pending` and `lastOutcome` stay as they were. A wrapper call then returns the original
 * 403 unchanged (`createStepUpFetch` step 6). An `approve` call then rejects with
 * `signal.reason`. An abort during a presentation reaches the presenter, which rejects with
 * `signal.reason`, and the caller meets that rejection.
 *
 * `wrap` keeps one wrapper per `base` in a `WeakMap`, so a `useEffect` dependency stays stable. A
 * new `origin` starts a new map. A new `presenter` or `refreshSession` reaches the existing
 * wrappers, because the queue reads the latest props.
 */
export function StepUpProvider(props) {
    const { presenter, origin, refreshSession, children } = props;
    const [pending, setPending] = useState(null);
    const [lastOutcome, setLastOutcome] = useState(null);
    const latest = useRef({ presenter, refreshSession });
    latest.current = { presenter, refreshSession };
    const queue = useRef(Promise.resolve());
    // One turn of the provider queue. It answers `null`, and shows nothing, when `signal` aborted
    // before the turn came: in the wait, or before the call.
    const presentInTurn = useCallback((request, signal) => {
        const turn = queue.current.then(async () => {
            if (signal.aborted)
                return null;
            const current = latest.current.presenter;
            if (current === null)
                return 'dismissed';
            setPending(request);
            try {
                const decision = await current.present(request, signal);
                setLastOutcome({ stepUpRequestId: request.stepUpRequestId, decision });
                return decision;
            }
            finally {
                setPending(null);
            }
        });
        queue.current = turn.catch(() => undefined);
        return turn;
    }, []);
    // The presenter of every wrapper. A call whose signal aborted in the wait gets `dismissed`, so
    // the wrapper returns the original 403 unchanged (`createStepUpFetch` step 6).
    const queued = useMemo(() => ({
        async present(request, signal) {
            return (await presentInTurn(request, signal)) ?? 'dismissed';
        },
    }), [presentInTurn]);
    const refresh = useCallback(() => (latest.current.refreshSession ?? refreshAndNotify)(), []);
    const wrappers = useMemo(() => ({ origin, byBase: new WeakMap() }), [origin]);
    const available = presenter !== null;
    const wrap = useCallback((base) => {
        const known = wrappers.byBase.get(base);
        if (known !== undefined)
            return known;
        const wrapped = createStepUpFetch(base, {
            presenter: queued,
            refreshSession: refresh,
            ...(wrappers.origin === undefined ? {} : { origin: wrappers.origin }),
        });
        wrappers.byBase.set(base, wrapped);
        return wrapped;
    }, [wrappers, queued, refresh]);
    const approve = useCallback(async (request, signal) => {
        const refusal = kernelRefusalOf(403, request, dataPlaneKernelOrigin(wrappers.origin));
        if (refusal?.kind !== 'step-up-required')
            return 'dismissed';
        const callSignal = signal ?? new AbortController().signal;
        const decision = await presentInTurn(refusal.body, callSignal);
        if (decision === null)
            throw callSignal.reason;
        if (decision === 'approved') {
            try {
                await refresh();
            }
            catch {
                // The caller replays with the header; a tier-1 approval needs no new token.
            }
        }
        return decision;
    }, [presentInTurn, refresh, wrappers]);
    // With no presenter the context carries `NO_STEP_UP`, so `wrap` and `approve` above run only
    // while a presenter exists. A queued turn re-reads the presenter, so a later `null` dismisses it.
    const value = useMemo(() => (available ? { available, pending, lastOutcome, wrap, approve } : NO_STEP_UP), [available, pending, lastOutcome, wrap, approve]);
    return _jsx(StepUpContext, { value: value, children: children });
}
/**
 * The step-up state of the nearest {@link StepUpProvider}. Outside a provider it answers the state
 * of no presenter: `available` is `false`, `wrap(base)` is `base`, and `approve` resolves
 * `dismissed`. Never throws.
 */
export function useStepUp() {
    return useContext(StepUpContext);
}
/** The current epoch second, the unit of `stepUpAt` and of `isWithinStepUpWindow`. */
function nowSeconds() {
    return Math.floor(Date.now() / 1000);
}
/**
 * The longest delay that `setTimeout` keeps, 2^31 - 1 ms. A longer delay fires at once in every
 * browser, so a `stepUpAt` far in the future gets a timer of this length and arms a new one when it
 * fires.
 */
const MAX_TIMER_DELAY_MS = 2_147_483_647;
/**
 * The epoch second at which `isStepUpWindowOpen(claims, second)` next changes its answer after
 * `now`, or `null` when it never changes again.
 *
 * - A `stepUpAt` after `now` (the client clock is behind core-api): `stepUpAt`, the open edge.
 * - An open window: `stepUpWindowClosesAt(claims) + 1`, the first closed second.
 * - No kernel session, no `stepUpAt`, or a window that closed: `null`.
 */
function nextWindowEdge(claims, now) {
    const closesAt = stepUpWindowClosesAt(claims);
    if (closesAt === null)
        return null;
    const opensAt = closesAt - STEP_UP_WINDOW_SECONDS;
    if (now < opensAt)
        return opensAt;
    if (now <= closesAt)
        return closesAt + 1;
    return null;
}
/**
 * The kernel session claims of `useAuth().token` on `origin`, and `stepUpWindowOpen` at render
 * time. `origin` defaults to `globalThis.location.origin`.
 *
 * While the answer can still change, it keeps one timer to the next edge of the window, and that
 * timer renders the component again. The edge is the open second for a `stepUpAt` ahead of the
 * client clock, and the first closed second for an open window. A timer that fires before its
 * edge (the clock stepped back) arms a new timer to the same edge. A delay over 2^31 - 1 ms gets
 * a timer of that length. It clears the timer when a new token or origin moves the edge, when no
 * edge is left, and at unmount, so an unmounted component renders nothing.
 *
 * Outside `AuthProvider` it throws the `useAuth` error. The answer is a UI hint: core-api decides
 * every operation.
 */
export function useKernelSession(origin) {
    const { token } = useAuth();
    const pageOrigin = origin ?? globalThis.location.origin;
    const claims = useMemo(() => kernelSessionClaimsOf(token, pageOrigin), [token, pageOrigin]);
    const [tick, setTick] = useState(0);
    const now = nowSeconds();
    const stepUpWindowOpen = isStepUpWindowOpen(claims, now);
    const edge = nextWindowEdge(claims, now);
    // `tick` is a dependency so that a timer which fired before its edge arms the next one.
    useEffect(() => {
        if (edge === null)
            return undefined;
        const delay = Math.min(edge * 1000 - Date.now(), MAX_TIMER_DELAY_MS);
        const timer = setTimeout(() => setTick((value) => value + 1), delay);
        return () => clearTimeout(timer);
    }, [edge, tick]);
    return { ...claims, stepUpWindowOpen };
}
//# sourceMappingURL=step-up-context.js.map